import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Wallet, LandPlot, FolderKanban, ClipboardCheck, Loader2, Check, X, Info, Sprout, TreePine, AlertTriangle, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, StatCard, StatusBadge, EmptyState, ResponsiveTable } from "../../components/dash/ui";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { MediaPreview } from "./AdminCrud";
import { VerifiedGroups } from "../legal/LegalDocs";
import { formatDate, formatDay, formatRupiah, ACTIVITY_TYPES } from "../../lib/labels";

const Spinner = () => <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>;

export const ActivityList = ({ items, testid }) =>
  !items.length ? <EmptyState text="Belum ada aktivitas." testid={`${testid}-empty`} /> : (
    <ul className="divide-y divide-slate-100 bg-white border border-slate-200/80 rounded-xl" data-testid={testid}>
      {items.map((a) => (
        <li key={a.id} className="px-5 py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 font-mono">{a.action}</p>
            <p className="text-xs text-slate-500 truncate">{a.userName || "Sistem / tidak dikenal"} · {a.entityType}</p>
          </div>
          <span className="text-xs text-slate-500 shrink-0">{formatDate(a.createdAt)}</span>
        </li>
      ))}
    </ul>
  );

export function AdminDashboard() {
  const [s, setS] = useState(null);
  const [groups, setGroups] = useState([]);
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/legal-summary").then((r) => setGroups(r.data)).catch(() => {});
    api.get("/admin/notifications", { params: { unread: true } }).then((r) => setUnread(r.data.unread)).catch(() => {});
  }, []);
  if (!s) return <Spinner />;
  const ps = s.projectsByStatus || {};
  const c = (...k) => k.reduce((t, x) => t + (ps[x] || 0), 0);
  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Coffee Plantation Command Center" subtitle="PT SJI memantau seluruh sistem. Operasional kebun dijalankan Kelompok Tani / Koperasi Tani sebagai pengelola lapangan." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <StatCard label="Semua Kebun" value={s.totalProjects} icon={FolderKanban} testid="stat-total-projects" />
        <StatCard label="Total Lahan Kopi" value={s.landParcels} icon={LandPlot} testid="stat-land" />
        <StatCard label="Total Hektare" value={s.totalHectares} icon={LandPlot} testid="stat-hectares" />
        <StatCard label="Lahan Tersedia untuk Investasi" value={s.landAvailable} icon={LandPlot} testid="stat-land-available" />
        <StatCard label="Lahan Proses Investasi" value={s.landInProcess} icon={LandPlot} testid="stat-land-process" />
        <StatCard label="Lahan Sudah Investasi" value={s.landInvested} icon={LandPlot} testid="stat-land-invested" />
        <StatCard label="Dalam Persiapan" value={c("DRAFT", "ACTIVE", "LAND_PREPARATION")} icon={FolderKanban} testid="stat-stage-prep" />
        <StatCard label="Dalam Penanaman" value={c("PLANTING")} icon={Sprout} testid="stat-stage-planting" />
        <StatCard label="Kebun dalam Perawatan" value={c("EARLY_GROWTH", "MAINTENANCE", "PRE_PRODUCTION")} icon={Sprout} testid="stat-stage-maintenance" />
        <StatCard label="Kebun Produktif" value={c("INITIAL_HARVEST", "PRODUCTIVE")} icon={Sprout} testid="stat-stage-productive" />
        <StatCard label="Total Investor" value={s.investors} icon={Wallet} testid="stat-investors" />
        <StatCard label="Total Kelompok Tani / Koperasi" value={s.farmerGroups} icon={Users} testid="stat-farmer-groups" />
        <StatCard label="Total Pemilik Lahan" value={s.landOwners} icon={Users} testid="stat-land-owners" />
        <StatCard label="Total Pohon Kopi Target" value={s.targetTrees?.toLocaleString("id-ID")} icon={TreePine} testid="stat-target-trees" />
        <StatCard label="Total Pohon Hidup" value={s.livingTrees?.toLocaleString("id-ID")} icon={TreePine} testid="stat-living-trees" />
        <StatCard label="Kebun Bermasalah" value={s.projectsWithIssues} icon={AlertTriangle} testid="stat-issues" />
        <StatCard label="Aktivitas & Media Menunggu Verifikasi" value={s.pendingVerification} icon={ClipboardCheck} testid="stat-pending" />
        <StatCard label="Minat Investasi Baru" value={s.newInterests} icon={Inbox} testid="stat-new-interests" />
        <StatCard label="Kebun Kopi dengan Pemodal" value={s.investmentProjects} icon={FolderKanban} testid="stat-investment-projects" />
        <StatCard label="Kelola Kebun Milik Pribadi" value={s.privateProjects} icon={FolderKanban} testid="stat-private-projects" />
        <StatCard label="Legalitas Menunggu Verifikasi" value={s.legalPending} icon={ClipboardCheck} testid="stat-legal-pending" />
        <StatCard label="Dokumen Lahan (Pemantauan, tanpa persetujuan)" value={s.landDocsPending} icon={ClipboardCheck} testid="stat-landdocs-pending" />
        <StatCard label="Pemilik Lahan Menunggu Persetujuan" value={s.pendingOwners} icon={Users} testid="stat-pending-owners" />
        <Link to="/dashboard/admin/notifikasi" data-testid="stat-password-notif-link"><StatCard label="Notifikasi Kata Sandi Baru" value={unread} icon={Users} testid="stat-password-notif" /></Link>
      </div>
      <div className="mt-8"><VerifiedGroups groups={groups} /></div>
      <div className="mt-10 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base md:text-lg font-semibold text-slate-900">Aktivitas Terbaru Platform</h2>
            <Link to="/dashboard/admin/audit-log" className="text-sm font-semibold text-emerald-800" data-testid="view-audit-log-link">Lihat semua</Link>
          </div>
          <ActivityList items={s.recentActivities} testid="recent-activities" />
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 h-fit" data-testid="phase-note">
          <Info className="h-5 w-5 text-blue-700" />
          <p className="mt-3 text-sm font-semibold text-blue-900">Belum diaktifkan</p>
          <p className="mt-1.5 text-sm text-blue-900/80 leading-relaxed">Bagi hasil, BEP, pembayaran investor, dan transaksi offtake final akan dibangun sebagai modul terpisah.</p>
        </div>
      </div>
    </div>
  );
}

const DecisionDialog = ({ item, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const send = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/approvals/${item.kind}/${item.row.id}`, { decision: item.decision, note });
      toast.success(item.decision === "APPROVED" ? "Data disetujui" : "Data ditolak");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="decision-dialog">
        <DialogHeader><DialogTitle>{item.decision === "APPROVED" ? "Setujui data" : "Tolak data"}</DialogTitle></DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (opsional)" data-testid="decision-note-input" />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="decision-cancel-btn">Batal</Button>
          <Button onClick={send} disabled={saving} className={item.decision === "APPROVED" ? "bg-emerald-800 hover:bg-emerald-900" : "bg-red-600 hover:bg-red-700"} data-testid="decision-confirm-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Konfirmasi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const decisionActions = (kind, open) => (row, p) => (
  <div className="inline-flex gap-2">
    <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900" onClick={() => open({ kind, row, decision: "APPROVED" })} data-testid={`${p}approve-${kind}-${row.id}`}><Check className="h-3.5 w-3.5 mr-1" /> Setujui</Button>
    {kind !== "media" && <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => open({ kind, row, decision: "REJECTED" })} data-testid={`${p}reject-${kind}-${row.id}`}><X className="h-3.5 w-3.5 mr-1" /> Tolak</Button>}
  </div>
);

export function AdminApprovals() {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(null);
  const load = useCallback(() => { api.get("/admin/approvals").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Spinner />;
  return (
    <div className="space-y-10" data-testid="admin-approvals">
      <PageHeader title="Persetujuan" subtitle="Tinjau data yang dikirim sebelum dapat dilihat investor." />
      <section>
        <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-4">Kegiatan Lapangan ({data.activities.length})</h2>
        <ResponsiveTable testid="approval-activities" rows={data.activities} empty="Tidak ada kegiatan yang menunggu."
          actions={(r, p) => <Button asChild size="sm" className="bg-emerald-800 hover:bg-emerald-900" data-testid={`${p}review-activity-${r.id}`}><Link to={`/dashboard/admin/kegiatan/${r.id}`}>Lihat & Tinjau</Link></Button>}
          columns={[{ key: "activityDate", label: "Tanggal", render: (r) => formatDay(r.activityDate) },
            { key: "activityType", label: "Kegiatan", render: (r) => ACTIVITY_TYPES[r.activityType] },
            { key: "projectCode", label: "Kebun" }, { key: "farmerGroupName", label: "Kelompok / Koperasi Tani" },
            { key: "totalCost", label: "Biaya", render: (r) => formatRupiah(r.totalCost) },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }]} />
      </section>
      <section>
        <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-4">Foto & Video ({data.media.length})</h2>
        <ResponsiveTable testid="approval-media" rows={data.media} empty="Tidak ada media yang menunggu." actions={decisionActions("media", setPending)}
          columns={[{ key: "p", label: "Pratinjau", render: (r) => <MediaPreview item={r} /> }, { key: "caption", label: "Keterangan" },
            { key: "projectCode", label: "Kebun" }, { key: "uploaderName", label: "Pengirim" }]} />
      </section>
      <section>
        <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-4">Verifikasi Lahan ({data.land.length})</h2>
        <ResponsiveTable testid="approval-land" rows={data.land} empty="Tidak ada lahan yang menunggu." actions={decisionActions("land", setPending)}
          columns={[{ key: "landCode", label: "Kode" }, { key: "name", label: "Nama" }, { key: "farmerGroupName", label: "Kelompok / Koperasi Tani" }, { key: "areaHectares", label: "Luas (ha)" }]} />
      </section>
      {pending && <DecisionDialog item={pending} onClose={() => setPending(null)} onDone={() => { setPending(null); load(); }} />}
    </div>
  );
}

const ENTITY_TYPES = ["", "User", "FarmerGroup", "LandParcel", "InvestmentProject", "InvestorProject", "FarmUpdate", "Media", "Document", "LegalDocument", "Settings"];
const changeText = (r) => (r.newValue ? JSON.stringify(r.newValue) : "-");

export function AdminAuditLog() {
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = { page, limit: 25, ...(entityType ? { entityType } : {}) };
    api.get("/admin/audit-logs", { params }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [page, entityType]);
  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  return (
    <div data-testid="admin-audit-log">
      <PageHeader title="Audit Log" subtitle="Catatan aktivitas penting di seluruh platform. Hanya dapat dilihat PT SJI." />
      <div className="mb-5 max-w-xs">
        <select value={entityType} onChange={(e) => { setEntityType(e.target.value); setPage(1); }} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="audit-entity-filter">
          {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t || "Semua entitas"}</option>)}
        </select>
      </div>
      {!data ? <Spinner /> : (
        <ResponsiveTable testid="audit-table" rows={data.items} empty="Belum ada log."
          columns={[{ key: "createdAt", label: "Waktu", render: (r) => <span className="whitespace-nowrap">{formatDate(r.createdAt)}</span> },
            { key: "userName", label: "Pengguna", render: (r) => r.userName || "-" },
            { key: "action", label: "Aksi", render: (r) => <span className="font-mono text-xs">{r.action}</span> },
            { key: "entityType", label: "Entitas" },
            { key: "newValue", label: "Perubahan", render: (r) => <span className="font-mono text-[11px] text-slate-500 break-all line-clamp-2 max-w-xs inline-block">{changeText(r)}</span> }]} />
      )}
      <div className="mt-5 flex items-center justify-end gap-3 text-sm">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-prev-btn">Sebelumnya</Button>
        <span data-testid="audit-page-info">{page} / {pages}</span>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="audit-next-btn">Berikutnya</Button>
      </div>
    </div>
  );
}

const SETTINGS_FIELDS = [
  ["platformName", "Nama platform"], ["contactEmail", "Email kontak"], ["contactPhone", "Telepon kontak"],
  ["address", "Alamat kantor"], ["announcement", "Pengumuman singkat"],
];

const BACKUP_STATUS = { SUCCESS: ["Berhasil & terverifikasi", "text-emerald-800"], FAILED: ["Gagal", "text-red-700"], RUNNING: ["Berjalan", "text-sky-700"], PURGED: ["Dihapus (lewat retensi)", "text-slate-500"] };
const wibTime = (iso) => (iso ? `${new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", dateStyle: "medium", timeStyle: "short" })} WIB` : "-");

const AutoBackupCard = () => {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/backup/runs").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);
  const runNow = async () => {
    try { await api.post("/admin/backup/run-now"); toast.success("Backup dimulai. Status diperbarui otomatis."); setTimeout(load, 1500); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const download = async (r) => {
    try {
      const res = await api.get(`/admin/backup/runs/${r.id}/download`, { responseType: "blob", timeout: 0 });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `salimbado-db-${r.startedAt.slice(0, 10)}.zip` });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("Backup gagal diunduh"); }
  };
  if (!d) return null;
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-5 sm:p-8 max-w-2xl space-y-3 mt-6" data-testid="auto-backup-card">
      <h2 className="font-bold text-slate-900">Backup Otomatis Harian</h2>
      <p className="text-sm text-slate-600" data-testid="auto-backup-policy">{d.schedule}. Database dienkripsi lalu disimpan di object storage terpisah dari database, kemudian diperiksa ulang (dekripsi + jumlah dokumen). Retensi: {d.retention}. Jika gagal, peringatan dikirim ke Kontak Keamanan dan muncul di Pusat Keamanan.</p>
      <p className="text-sm" data-testid="auto-backup-last">Backup sukses terakhir: <b>{wibTime(d.lastSuccessAt)}</b></p>
      <Button type="button" variant="outline" onClick={runNow} disabled={d.runs.some((r) => r.status === "RUNNING")} data-testid="auto-backup-run-btn">Backup Sekarang</Button>
      <ul className="divide-y divide-slate-100 text-sm" data-testid="auto-backup-runs">
        {d.runs.slice(0, 10).map((r) => (
          <li key={r.id} className="py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`backup-run-${r.id}`}>
            <span>{wibTime(r.startedAt)} · {r.kind === "MONTHLY" ? "Bulanan" : "Harian"} · {r.trigger === "JADWAL" ? "terjadwal" : "manual"}{r.documents != null ? ` · ${r.documents} dokumen · ${(r.sizeBytes / 1024).toFixed(0)} KB` : ""}</span>
            <span className={`font-semibold ${BACKUP_STATUS[r.status]?.[1]}`}>{BACKUP_STATUS[r.status]?.[0]}{r.error ? `: ${r.error}` : ""}</span>
            {r.status === "SUCCESS" && <Button size="sm" variant="ghost" onClick={() => download(r)} data-testid={`backup-download-${r.id}`}>Unduh</Button>}
          </li>
        ))}
        {!d.runs.length && <li className="py-2 text-slate-500">Belum ada backup otomatis.</li>}
      </ul>
    </div>
  );
};

const BackupCard = () => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/backup", { responseType: "blob", timeout: 0 });
      const name = (r.headers["content-disposition"] || "").match(/filename="?([^";]+)/)?.[1] || "salimbado-backup.zip";
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      const failed = Number(r.headers["x-backup-failed"] || 0);
      if (failed) toast.warning(`Backup terunduh, tetapi ${failed} file gagal disertakan (lihat manifest.json)`);
      else toast.success(`Backup terunduh: ${name} (${Number(r.headers["x-backup-files"] || 0)} file)`);
    } catch (e) { toast.error("Backup gagal dibuat. Coba lagi beberapa saat."); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-5 sm:p-8 max-w-2xl space-y-3 mt-6" data-testid="backup-card">
      <h2 className="font-bold text-slate-900">Backup Data</h2>
      <p className="text-sm text-slate-600">Unduh satu file ZIP berisi seluruh database dan semua file upload (foto, dokumen, bukti). Simpan ke Google Drive PT SJI di folder yang aksesnya dibatasi, karena berisi data pribadi. Cara restore ada di README.txt dalam ZIP dan docs/BACKUP_RESTORE.md.</p>
      <Button type="button" onClick={run} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid="backup-download-btn">
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{busy ? "Menyiapkan backup..." : "Unduh Backup Lengkap"}
      </Button>
    </div>
  );
};

export function AdminSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setForm(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(SETTINGS_FIELDS.map(([k]) => [k, form[k] || ""]));
      await api.put("/admin/settings", payload);
      toast.success("Pengaturan disimpan");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  if (!form) return <Spinner />;
  return (
    <div data-testid="admin-settings">
      <PageHeader title="Pengaturan" subtitle="Informasi umum platform." />
      <form onSubmit={save} className="bg-white border border-slate-200/80 rounded-xl p-5 sm:p-8 max-w-2xl space-y-5">
        {SETTINGS_FIELDS.map(([k, label]) => (
          <div key={k} className="space-y-1.5">
            <Label htmlFor={k}>{label}</Label>
            <Input id={k} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required={k === "platformName"} data-testid={`settings-${k}-input`} />
          </div>
        ))}
        <Button type="submit" disabled={saving} className="bg-emerald-800 hover:bg-emerald-900" data-testid="settings-save-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Simpan Pengaturan
        </Button>
      </form>
      <SecurityEmailCard />
      <AutoBackupCard />
      <BackupCard />
    </div>
  );
}

const SecurityEmailCard = () => {
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/security/settings").then((r) => setEmails(r.data.alertEmails.join(", "))).catch((e) => toast.error(formatApiError(e))); }, []);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const save = (e) => { e.preventDefault(); run(async () => setEmails((await api.put("/admin/security/settings", { alertEmails: emails })).data.alertEmails.join(", ")), "Email keamanan disimpan"); };
  const test = () => run(() => api.post("/admin/security/settings/test-email"), "Email uji dikirim ke kontak keamanan");
  return (
    <form onSubmit={save} className="mt-6 bg-white border border-slate-200/80 rounded-xl p-5 sm:p-8 max-w-2xl space-y-3" data-testid="security-email-card">
      <h3 className="font-bold text-slate-900">Kontak Keamanan Resmi PT SJI</h3>
      <p className="text-sm text-slate-600">Setiap peringatan berisiko KRITIS di Pusat Keamanan otomatis dikirim ke alamat ini (maksimal 5, pisahkan dengan koma). Email tidak memuat kata sandi, OTP, atau token.</p>
      <Input value={emails} onChange={(e) => setEmails(e.target.value)} required data-testid="security-emails-input" />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid="security-emails-save-btn">Simpan Kontak Keamanan</Button>
        <Button type="button" variant="outline" disabled={busy} onClick={test} data-testid="security-emails-test-btn">Kirim Email Uji</Button>
      </div>
    </form>
  );
};

export const StatusPill = StatusBadge;
